import React, { useMemo } from "react";
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { useAtomValue } from "jotai";
import { Card, CardContent, Typography } from "@mui/material";

import { eachConditionDataByKeyAtom, EachConditionDataKeys } from "atoms/Rtool/cabinAtoms";
import { Series } from "utils/charts/types";
import { useRechartsLegendSync } from "utils/charts/useRechartsLegendSync";
import { HoverLegend } from "utils/charts/HoverLegend";
import { lockReferenceLine } from "utils/charts/lockReferenceLine";
import { buildIndexMap, makeTicksFromData } from "utils/charts/AxisLabel";
import { toChartData } from "utils/charts/toChartData";
import { phenomenonIdAtom } from "atoms/Rtool/stageConfigAtoms";

interface Props {
    title: string;
    graph_id: EachConditionDataKeys;
}

const SERIES_LIST: Series[] = [
    { key: "cabin_noise_target", name: "室内音目標値", color: "#ff2c95ff" },
    { key: "cabin_noise_pred", name: "室内音計算値", color: "#006affff" },
    { key: "background_noise_target", name: "暗騒音目標", color: "#25c625ff" },
    { key: "cabin_noise_meas", name: "室内音実測値", color: "#000000ff" },
    { key: "deltaL_target", name: "ΔL目標", color: "#d69027ff" },
];

export const DisplayCabinNoisePredBackground: React.FC<Props> = ({ title, graph_id }) => {
    // フックは条件分岐より先に・無条件で呼ぶ
    const memoAtom = useMemo(() => eachConditionDataByKeyAtom(graph_id), [graph_id]);
    const target = useAtomValue(memoAtom);

    const ph = useAtomValue(phenomenonIdAtom);
    const phenomenonGroupId = ph ? ph.split("_")[1] : "";
    const indexScale = phenomenonGroupId === "aco" ? 10 : 1;

    // target の状態に関わらず呼ぶ（失敗時は空配列）
    const chartData = useMemo(() => {
        if (target && target.status === "success") return toChartData(target, indexScale);
        return [];
    }, [target, indexScale]);

    // 外部凡例同期（他チャートのホバーでも更新）
    const { setIndexRef, HiddenSyncTooltip, handlers, lockedValue } = useRechartsLegendSync(chartData, {
        xKey: "index",
        resetOnData: true,
    });

    // 状態判定はフック呼び出しの「後」に行う
    if (!target) {
        return <Typography variant="body2" align="center">データなし</Typography>;
    }
    if (target.status === "notstarted" || target.status === "loading") {
        return <Typography variant="body2" align="center">読み込み中...</Typography>;
    }
    if (target.status === "error") {
        return <Typography variant="body2" align="center" color="error">データ読み込み中にエラーが発生しました。</Typography>;
    }
    if (!chartData.length) {
        return <Typography variant="body2" align="center">データなし</Typography>;
    }

    const ticks = makeTicksFromData(chartData, "index", 250 * indexScale);
    const rpmByIndex = buildIndexMap(chartData, "index", "rpm");
    const velByIndex = buildIndexMap(chartData, "index", "vel");
    console.log("chartData", chartData);

    return (
        <Card key={title} variant="outlined">
            <CardContent>
                <Typography variant="subtitle1" gutterBottom>{title}</Typography>

                {/* 値表示は外部凡例に集約 */}
                <HoverLegend seriesList={SERIES_LIST} data={chartData as any[]} setIndexRef={setIndexRef} />

                <ResponsiveContainer width="100%" height={240}>
                    <LineChart
                        syncId="rTooltip"
                        syncMethod="value"
                        data={chartData}
                        onMouseMove={handlers.onMouseMove}
                        onMouseLeave={handlers.onMouseLeave}
                        onClick={handlers.onClick}
                    >
                        <XAxis
                            dataKey="index"
                            xAxisId="0"
                            height={18}
                            ticks={ticks}
                            tick={{ fontSize: 8 }}
                            label={{ value: "Frequency", position: "insideLeft", offset: -20, fontSize: 8 }}
                        />
                        <XAxis
                            dataKey="index"
                            xAxisId="1"
                            height={18}
                            ticks={ticks}
                            tick={{ fontSize: 8 }}
                            tickFormatter={(v: number) =>
                                rpmByIndex.has(v) ? Math.round(rpmByIndex.get(v)!).toString() : ""
                            }
                            label={{ value: "RPM", position: "insideLeft", offset: -20, fontSize: 8 }}
                        />
                        <XAxis
                            dataKey="index"
                            xAxisId="2"
                            height={18}
                            ticks={ticks}
                            tick={{ fontSize: 8 }}
                            tickFormatter={(v: number) =>
                                velByIndex.has(v) ? Math.round(velByIndex.get(v)!).toString() : ""
                            }
                            label={{ value: "Velocity", position: "insideLeft", offset: -20, fontSize: 8 }}
                        />

                        <YAxis tick={{ fontSize: 10 }} />
                        {/* 同期イベント受け取り用（非表示） */}
                        {HiddenSyncTooltip}

                        {SERIES_LIST.map((s) => (
                            <Line
                                key={s.key}
                                type="monotone"
                                dataKey={s.key}
                                name={s.name}
                                stroke={s.color}
                                strokeWidth={3}
                                dot={false}
                                opacity={0.8}
                                activeDot={{ r: 6 }}
                            />
                        ))}
                        {lockReferenceLine(lockedValue)}
                    </LineChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};
